import { Button } from '@insify/design-system/components';
import { headingSm } from '@insify/design-system/typography';
import { Money, formatMoney } from '@insify/global-money';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import ReactMarkdown, { MarkdownHooks } from 'react-markdown';
import rehypeClassNames from 'rehype-class-names';
import rehypeUnwrapImages from 'rehype-unwrap-images';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';

import { ProgressBar } from '../../../../components/ProgressBar/ProgressBar';
import { remarkCustomDirectives } from '../../../../utils/customDirectives';

import styles from './Markdown.module.css';

import type { ProgressBarProps } from '../../../../components/ProgressBar/ProgressBar';
import type { FormatMoneyOptions } from '@insify/global-money';
import type { BarChartProps, LineChartProps, PieChartProps } from '@mui/x-charts';
import type { ComponentType } from 'react';
import type { Components } from 'react-markdown';

type MarkdownProps = {
  isPrint?: boolean; // Optional prop to indicate if the markdown is being rendered for print
  children: string;
};

const classMap = {
  h1: headingSm,
  table: styles.table,
  th: styles.tableHeader,
  td: styles.tableCell,
};

type ButtonProps = {
  to?: string;
  kind?: React.ComponentProps<typeof Button>['kind'];
  onClick?: () => void;
  iconSize?: number;
  strokeWidth?: number;
  icon: React.ReactElement;
  trackingId: string;
  layout?: 'wide' | 'narrow';
  children?: React.ReactNode;
};

// Create your own extended Components type
type ExtendedComponents = Components & {
  progressbar?: ComponentType<ProgressBarProps>; // Your custom tag/component
  barchart?: ComponentType<BarChartProps>;
  linechart?: ComponentType<LineChartProps>;
  piechart?: ComponentType<PieChartProps>;
  money?: ComponentType<{ value: number; inCents: boolean } & FormatMoneyOptions>;
  custombutton?: ComponentType<ButtonProps>;
};

// Helper to parse props from directive string prop to their respective types
// Should be used with care
const parseProps = <T extends object>(props: T): T => {
  // Create a shallow copy so we can mutate
  const parsedProps: T = { ...props };
  (Object.keys(parsedProps) as (keyof T)[]).forEach((key) => {
    try {
      parsedProps[key] = JSON.parse(parsedProps[key] as string) as T[typeof key];
    } catch {
      // do nothing if parsing fails
    }
  });
  console.log(parsedProps);
  return parsedProps;
};

const components: ExtendedComponents = {
  ul(props) {
    const { children, ref: _ref, node: _node, ...rest } = props;
    return (
      <ul style={{ listStyleType: 'disc', listStylePosition: 'inside', paddingLeft: '10px' }} {...rest}>
        {children}
      </ul>
    );
  },
  money(props) {
    const parsedProps = parseProps(props);
    const moneyField = Money.fromField({
      cents: `${parsedProps.inCents ? parsedProps.value : parsedProps.value * 100}`,
      currency: 'EUR',
    });
    return (
      <span>
        {formatMoney(moneyField.toField(), 'eur', {
          withoutCurrency: parsedProps.withoutCurrency || false,
          withoutFraction: parsedProps.withoutFraction || false,
          withoutGrouping: parsedProps.withoutGrouping || false,
        })}
      </span>
    );
  },
  custombutton(props) {
    const { children, ...rest } = props;
    return (
      <Button type='button' kind='neutral' {...rest}>
        {children}
      </Button>
    );
  },
  img(props) {
    const { src, alt, ref: _ref, node: _node, ...rest } = props;
    return (
      // <div
      //   aria-label={alt}
      //   style={{ backgroundImage: `url(${src})`, backgroundSize: 'cover', width: '100%', height: '100%' }}
      //   {...rest}
      // ></div>
      <img aria-label={alt} src={src} style={{ width: '100%', height: '100%' }} {...rest}></img>
    );
  },
  progressbar(props) {
    return <ProgressBar {...props} />;
  },
  barchart(props) {
    const parsedProps = parseProps<BarChartProps>(props);
    return <BarChart {...parsedProps} />;
  },
  linechart(props) {
    const parsedProps = parseProps<LineChartProps>(props);
    return <LineChart {...parsedProps} />;
  },
  piechart(props) {
    const parsedProps = parseProps<PieChartProps>(props);
    return <PieChart {...parsedProps} />;
  },
  table: (props) => {
    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table} {...props} />
      </div>
    );
  },
  p: (props) => {
    const { children, ref: _ref, node: _node, ...rest } = props;
    return (
      <p style={{ lineHeight: '1.5', width: '100%' }} {...rest}>
        {children}
      </p>
    );
  },
};

export const Markdown: React.FC<MarkdownProps> = ({ isPrint, children }) => {
  // If isPrint is true, we can modify the components or styles as needed
  if (isPrint) {
    // For example, we might want to remove certain components or change styles
    components.button = () => {
      return null; // Remove buttons in print mode
    };
  }
  return (
    <>
      <MarkdownHooks components={components} />
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkDirective, remarkCustomDirectives]}
        components={components}
        rehypePlugins={[[rehypeClassNames, classMap], rehypeUnwrapImages]}
      >
        {children}
      </ReactMarkdown>
    </>
  );
};
